/**
 * Marquee Block
 * Builds a duplicated CSS marquee track that loops infinitely.
 */

const initializedMarquees = new WeakSet();
const MOBILE_BREAKPOINT = 768;

class MarqueeBlock {
  constructor(element) {
    if (initializedMarquees.has(element)) return;

    initializedMarquees.add(element);
    this.element = element;
    this.track = element.querySelector('.marquee-track');
    this.sourceGroup = element.querySelector('.marquee-group');
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    if (!this.track || !this.sourceGroup) return;

    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);

    this.build();
  }

  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  clearClones() {
    this.track.querySelectorAll('.marquee-group[aria-hidden="true"]').forEach((clone) => clone.remove());
  }

  build() {
    this.clearClones();

    if (!this.shouldBeActive()) {
      this.element.removeAttribute('data-marquee-ready');
      this.element.style.removeProperty('--marquee-shift');
      return;
    }

    const gap = parseInt(this.element.dataset.spaceBetween, 10) || 120;
    const duration = parseInt(this.element.dataset.speed, 10) || 3000;
    const baseWidth = this.sourceGroup.scrollWidth;

    if (!baseWidth) {
      this.element.removeAttribute('data-marquee-ready');
      return;
    }

    const minimumTrackWidth = this.element.clientWidth + baseWidth + gap;
    let safetyCounter = 0;

    do {
      const clone = this.sourceGroup.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
      safetyCounter += 1;
    } while (this.track.scrollWidth < minimumTrackWidth && safetyCounter < 8);

    this.element.style.setProperty('--marquee-gap', `${gap}px`);
    this.element.style.setProperty('--marquee-duration', `${duration}ms`);
    this.element.style.setProperty('--marquee-shift', `-${baseWidth + gap}px`);
    this.element.setAttribute('data-marquee-ready', 'true');
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.build();
    }, 120);
  }
}

function initMarqueeBlocks() {
  document.querySelectorAll('[data-marquee-block]').forEach((marquee) => {
    new MarqueeBlock(marquee);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMarqueeBlocks);
} else {
  initMarqueeBlocks();
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    initMarqueeBlocks();
  }
});

window.MarqueeBlock = MarqueeBlock;
window.initMarqueeBlocks = initMarqueeBlocks;
